import React, { useState, useEffect, useRef } from 'react'; // Added useRef
import { createPortal } from 'react-dom';
import { X, Settings, Plus } from 'lucide-react';
// Assuming App.css is imported in App.js and contains .sticky-note and related styles

const StickyNote = ({ id, text, color, x, y, onUpdateNotePosition, onUpdateNoteText, onUpdateNoteColor, onDeleteNote, isActive, onSetActive, isMobile, isOverlay }) => { // Added isActive, onSetActive, isMobile, isOverlay
  const [isDragging, setIsDragging] = useState(false);
  const pointerIdRef = useRef(null);
  const touchIdRef = useRef(null); // Added ref for active touch identifier
  const initialOffsetRef = useRef({ x: 0, y: 0 });
  const noteRef = useRef(null);
  // const colorInputRef = useRef(null); // Removed
  const [isEditing, setIsEditing] = useState(false);
  // Unified color picker state
  const [isColorPickerActive, setIsColorPickerActive] = useState(false);
  const [draftColor, setDraftColor] = useState(color || '#fefc92');
  // Removed showColorPickerDropdown, isColorPickerModalOpen
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Effect to check for mobile view on resize
  useEffect(() => {
    const checkMobile = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    checkMobile();
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Predefined colors for the picker
  const predefinedColors = ['#fefc92', '#ffadad', '#ffd6a5', '#fdffb6', '#caffbf', '#9bf6ff', '#a0c4ff', '#bdb2ff', '#ffc6ff'];

  // Pointer Event Handlers
  const handlePointerDown = (e) => {
    if (isMobile) return; // Disable internal drag on mobile (handled by dnd-kit)

    // Drag only if the mousedown/touchstart is on the .sticky-note div itself (padding area)
    if (e.target !== e.currentTarget) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0) {
      return;
    }
    if (isDragging) return;

    onSetActive(id);
    e.currentTarget.setPointerCapture(e.pointerId);
    pointerIdRef.current = e.pointerId;
    setIsDragging(true);
    initialOffsetRef.current = { x: e.clientX - x, y: e.clientY - y };
  };

  const interactiveSelectors = [
    '.delete-note-button', '.gear-icon',
    '.sticky-content textarea', // More specific to avoid issues if other textareas exist
    '.color-picker-modal-overlay' // And its children
    // '.color-picker', '.color-option', '.color-option-modal', '.color-input-modal', '.close-modal-button'
    // These are part of the modal, so .color-picker-modal-overlay should cover them.
  ];

  const handleTouchStart = (e) => {
    if (isMobile) return; // Disable internal drag on mobile

    if (e.target !== e.currentTarget) {
      return;
    }
    if (isDragging || pointerIdRef.current) {
      return;
    }
    const touch = e.changedTouches[0];
    if (!touch) return;

    onSetActive(id); // Added
    setIsDragging(true);
    touchIdRef.current = touch.identifier;
    initialOffsetRef.current = { x: touch.clientX - x, y: touch.clientY - y };
  };

  useEffect(() => {
    // Pointer Event Handlers
    const handlePointerMoveOnWindow = (e) => {
      if (!pointerIdRef.current || e.pointerId !== pointerIdRef.current) return;
      const newX = e.clientX - initialOffsetRef.current.x;
      const newY = e.clientY - initialOffsetRef.current.y;
      onUpdateNotePosition(id, newX, newY);
    };

    const handlePointerUpOnWindow = (e) => {
      if (!pointerIdRef.current || e.pointerId !== pointerIdRef.current) return;
      if (noteRef.current) {
        try { noteRef.current.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      }
      pointerIdRef.current = null;
      // Only set isDragging to false if a touch drag isn't also trying to run
      if (!touchIdRef.current) {
        setIsDragging(false);
        onSetActive(null); // Added
      }
    };

    // Touch Event Handlers
    const handleTouchMoveOnWindow = (e) => {
      if (!touchIdRef.current) return;
      const touch = Array.from(e.changedTouches).find(t => t.identifier === touchIdRef.current);
      if (!touch) return;

      e.preventDefault(); // Prevent scrolling during touch drag

      const newX = touch.clientX - initialOffsetRef.current.x;
      const newY = touch.clientY - initialOffsetRef.current.y;
      onUpdateNotePosition(id, newX, newY);
    };

    const handleTouchEndOnWindow = (e) => {
      if (!touchIdRef.current) return;
      const touchEnded = Array.from(e.changedTouches).find(t => t.identifier === touchIdRef.current);
      if (!touchEnded) return; // This end event is for a different touch

      touchIdRef.current = null;
      // Only set isDragging to false if a pointer drag isn't also trying to run
      if (!pointerIdRef.current) {
        setIsDragging(false);
        onSetActive(null); // Added
      }
    };

    if (isDragging) {
      if (pointerIdRef.current) { // Pointer-initiated drag
        window.addEventListener('pointermove', handlePointerMoveOnWindow);
        window.addEventListener('pointerup', handlePointerUpOnWindow);
        window.addEventListener('pointercancel', handlePointerUpOnWindow);
      } else if (touchIdRef.current) { // Touch-initiated drag
        window.addEventListener('touchmove', handleTouchMoveOnWindow, { passive: false });
        window.addEventListener('touchend', handleTouchEndOnWindow);
        window.addEventListener('touchcancel', handleTouchEndOnWindow);
      }
    }

    return () => {
      // Cleanup both sets of listeners
      window.removeEventListener('pointermove', handlePointerMoveOnWindow);
      window.removeEventListener('pointerup', handlePointerUpOnWindow);
      window.removeEventListener('pointercancel', handlePointerUpOnWindow);
      window.removeEventListener('touchmove', handleTouchMoveOnWindow);
      window.removeEventListener('touchend', handleTouchEndOnWindow);
      window.removeEventListener('touchcancel', handleTouchEndOnWindow);
    };
  }, [isDragging, onUpdateNotePosition, id, x, y, noteRef, initialOffsetRef, pointerIdRef, touchIdRef, setIsDragging]);


  const handleTextChange = (e) => {
    onUpdateNoteText(id, e.target.value);
  };

  const openColorPicker = (e) => { // Renamed and refactored
    e.stopPropagation();
    setDraftColor(color || '#fefc92'); // Initialize draftColor with current note color
    setIsColorPickerActive(true);
  };

  // Removed handleColorChange function

  // Prevent drag from starting when interacting with textarea or color picker
  const stopPropagation = (e) => e.stopPropagation();

  // Removed handleNoteClick function

  const handleDeleteClick = (e) => {
    e.stopPropagation(); // Prevent any other note interactions
    onDeleteNote(id);    // Call the onDeleteNote prop with the note's id
  };

  // Removed handleSpectrumBarClick function

  return (
    <div // This is now the main .sticky-note div
      ref={noteRef}
      className={['sticky-note', isActive ? 'active' : '', isDragging || isOverlay ? 'dragging' : ''].filter(Boolean).join(' ')}
      style={{
        position: 'absolute',
        left: `${x}px`,
        top: `${y}px`,
        backgroundColor: color || '#fefc92',
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        // CSS will handle padding, border-radius, width, height, box-sizing for .sticky-note
      }}
      onPointerDown={handlePointerDown}
      onTouchStart={handleTouchStart}
    >
      {/* Icons are direct children, will be absolutely positioned via CSS relative to .sticky-note */}
      <div className="delete-note-button" onClick={handleDeleteClick}>
        <X size={18} />
      </div>
      <div className="gear-icon"
        onClick={(e) => { stopPropagation(e); openColorPicker(e); }}
        onMouseDown={stopPropagation}>
        <Settings size={18} />
      </div>

      {/* Inner content area that will get the editing outline and internal padding */}
      <div className={`sticky-content ${isEditing ? 'editing' : ''}`}>
        <textarea
          value={text}
          onClick={stopPropagation} // To prevent clicks on textarea from triggering note drag
          onMouseDown={stopPropagation} // To prevent drags on textarea from triggering note drag
          onChange={handleTextChange}
          placeholder="Write your note..."
          onFocus={() => setIsEditing(true)}
          onBlur={() => setIsEditing(false)}
        // className for textarea will be handled by CSS: .sticky-content textarea
        />
      </div>

      {/* Color Picker Modal - direct child of .sticky-note */}
      {/* Color Picker Modal - direct child of .sticky-note */}
      {/* Color Picker Modal - Portalled to body to escape sticky note stacking context */}
      {isColorPickerActive && createPortal(
        <div className="color-picker-modal-overlay" onClick={() => setIsColorPickerActive(false)}>
          <div className="color-picker-modal-content unified-color-picker" onClick={stopPropagation}>
            <h3>Choose a Color</h3>

            <div className="color-options-grid">
              {/* Predefined Colors */}
              {predefinedColors.map(c => (
                <div
                  key={c}
                  className={`color-option-modal ${draftColor === c ? 'selected' : ''}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setDraftColor(c)}
                />
              ))}

              {/* Custom Color Button with Invisible Input Overlay */}
              <div
                className={`color-option-modal custom-color-add-btn ${!predefinedColors.includes(draftColor) ? 'selected' : ''}`}
                title="Custom Color"
                style={{
                  position: 'relative',
                  backgroundColor: !predefinedColors.includes(draftColor) ? draftColor : undefined
                }}
              >
                <Plus size={24} strokeWidth={2.5} style={{ opacity: 0.5 }} />

                <input
                  type="color"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    padding: 0,
                    border: 'none'
                  }}
                  value={draftColor}
                  onChange={(e) => setDraftColor(e.target.value)}
                />
              </div>
            </div>

            <div className="color-picker-actions-modern">
              <button
                className="btn-picker-primary"
                onClick={() => {
                  onUpdateNoteColor(id, draftColor);
                  setIsColorPickerActive(false);
                }}
              >
                Done
              </button>
              <button
                className="btn-picker-cancel"
                onClick={() => setIsColorPickerActive(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div> // End of .sticky-note div
  );
};

export default StickyNote;
